import { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useBOAuth } from '../../context/BOAuthContext';
import { toast } from 'sonner';
import {
  LayoutDashboard,
  Users,
  Users2,
  MapPin,
  ShoppingCart,
  CreditCard,
  Receipt,
  BarChart3,
  UserCog,
  FileText,
  LogOut,
  Menu,
  X,
  Building2,
  CheckSquare,
  FolderOpen,
} from 'lucide-react';

const navItems = [
  { key: 'dashboard', label: 'Dashboard', icon: LayoutDashboard, path: '/backoffice', resource: 'dashboard' },
  { key: 'realtors', label: 'Realtors', icon: Users2, path: '/backoffice/realtors', resource: 'users' },
  { key: 'clients', label: 'Clients', icon: Users, path: '/backoffice/clients', resource: 'clients' },
  { key: 'plots', label: 'Plots', icon: MapPin, path: '/backoffice/plots', resource: 'plots' },
  { key: 'sales', label: 'Sales', icon: ShoppingCart, path: '/backoffice/sales', resource: 'sales' },
  { key: 'payments', label: 'Payments', icon: CreditCard, path: '/backoffice/payments', resource: 'payments' },
  { key: 'development_fees', label: 'Dev Fees', icon: Receipt, path: '/backoffice/development-fees', resource: 'development_fees' },
  { key: 'allocations', label: 'Allocations', icon: CheckSquare, path: '/backoffice/allocations', resource: 'clients' },
  { key: 'documents', label: 'Documents', icon: FolderOpen, path: '/backoffice/documents', resource: 'clients' },
  { key: 'office_expenses', label: 'Office Expenses', icon: Receipt, path: '/backoffice/office-expenses', resource: 'office_expenses' },
  { key: 'expenses', label: 'Expenses', icon: Receipt, path: '/backoffice/expenses', resource: 'expenses' },
  { key: 'financial', label: 'Financials', icon: BarChart3, path: '/backoffice/financial', resource: 'financial' },
  { key: 'reports', label: 'Reports', icon: FileText, path: '/backoffice/reports', resource: 'reports' },
  { key: 'users', label: 'Users', icon: UserCog, path: '/backoffice/users', resource: 'users' },
];

const roleLabels = {
  owner: 'MD',
  sales_admin: 'Sales Admin',
  accountant: 'Accountant',
};

export const BOLayout = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, role, logout, hasAccess } = useBOAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const mainRef = useRef(null);

  // Scroll the main content area to top whenever the route changes.
  // Necessary because <main> is the scroll container (not window),
  // so React Router's default scroll-restoration doesn't apply.
  useEffect(() => {
    if (mainRef.current) {
      mainRef.current.scrollTo({ top: 0, behavior: 'auto' });
    }
    // Close the mobile sidebar after navigating.
    setSidebarOpen(false);
  }, [location.pathname]);

  const handleLogout = () => {
    logout();
    navigate('/backoffice/login');
    toast.success('Logged out successfully');
  };

  // Drive sidebar visibility entirely from role permissions in BOAuthContext.
  // Items the user can't access are hidden — never shown disabled.
  const visibleItems = navItems.filter((item) => hasAccess(item.resource));

  const isActive = (path) => {
    if (path === '/backoffice') return location.pathname === '/backoffice';
    return location.pathname.startsWith(path);
  };

  // Set page title for back-office
  useEffect(() => {
    document.title = 'Medichi Homes and Shelter';
    return () => { document.title = 'Medichi Tribe | Real Estate Recruitment Network'; };
  }, []);

  return (
    <div className="flex h-screen overflow-hidden bg-neutral-background" data-testid="bo-layout">
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 md:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`fixed md:relative z-50 w-64 h-full flex flex-col sidebar-dark transform transition-transform duration-200 ease-in-out ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0'
        }`}
      >
        <div className="p-6 border-b border-primary-800">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="w-10 h-10 rounded-full bg-primary-700 flex items-center justify-center">
                <Building2 className="h-5 w-5 text-white" />
              </div>
              <div>
                <span className="font-serif text-xl text-white block">Medici Homes</span>
                <span className="text-xs text-primary-400">Back Office</span>
              </div>
            </div>
            <button
              onClick={() => setSidebarOpen(false)}
              className="md:hidden p-2 rounded-lg text-primary-200 hover:bg-primary-800 hover:text-white"
              data-testid="bo-close-sidebar"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        <nav className="flex-1 p-4 overflow-y-auto">
          <div className="space-y-1">
            {visibleItems.map((item) => {
              const Icon = item.icon;
              return (
                <button
                  key={item.key}
                  onClick={() => { navigate(item.path); setSidebarOpen(false); }}
                  className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-colors ${
                    isActive(item.path)
                      ? 'bg-primary-800 text-white'
                      : 'text-primary-200 hover:bg-primary-800 hover:text-white'
                  }`}
                  data-testid={`bo-nav-${item.key}`}
                >
                  <Icon className="h-5 w-5" />
                  {item.label}
                </button>
              );
            })}
          </div>
        </nav>

        <div className="p-4 border-t border-primary-800">
          <div className="px-4 py-3 mb-3">
            <p className="text-primary-300 text-sm">{roleLabels[role] || role}</p>
            <p className="text-white font-medium truncate">{user?.full_name}</p>
          </div>
          <button
            onClick={handleLogout}
            className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-primary-200 hover:bg-primary-800 hover:text-white transition-colors"
            data-testid="bo-logout-btn"
          >
            <LogOut className="h-5 w-5" />
            Logout
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main ref={mainRef} className="flex-1 overflow-y-auto">
        {/* Mobile header */}
        <div className="md:hidden sticky top-0 z-30 bg-white border-b border-neutral-200 px-4 py-3 flex items-center justify-between">
          <button
            onClick={() => setSidebarOpen(true)}
            className="p-2 rounded-lg hover:bg-neutral-100"
            data-testid="bo-open-sidebar"
          >
            <Menu className="h-5 w-5" />
          </button>
          <span className="font-serif text-lg font-semibold">Medici Homes</span>
          <div className="w-9" />
        </div>

        <div className="p-4 sm:p-6 lg:p-8">
          {children}
        </div>
      </main>
    </div>
  );
};
